/**
 * The project workspace's tabs (design "Flow Project Tabs", utils/projectTabsModel.js has which ones a person sees):
 * Home · Preview · Code, the advanced tabs on the row (API, Data, Traces, Quality: pinned, shown by themselves, or
 * opened for now), then History (Changes, Activity and Sessions as three views of one tab, always), and More.
 * On a phone: Home · Preview · Code · History · More. Home (id 'dashboard', its old name: `?tab=dashboard` and
 * `?tab=home` both land on the page), Code and Preview are the project's own pages (Home, a service's editor or its
 * preview); API (try the services' APIs, components/api), Data (the project's datastores, components/data), Traces
 * (how the project's services talk to each other, from the requests: components/traces), Quality (tests, lint,
 * vulnerabilities, secrets, licences and the release gate: components/quality), Terminal (shells in the sandbox,
 * components/terminal/TerminalTabView), Git (each service's repo: commit, tag, push, pull, components/git/GitTab), Changes, Activity and
 * Sessions are workspace views shown in place of them (`view`): the old History dialog's Timeline and By person are
 * the two views of Changes, its Activity is the Activity view.
 *
 * A view is `{ tab: 'api' | 'mcp' | 'data' | 'traces' | 'quality' | 'config' | 'mail' | 'terminal' | 'git' | 'changes' | 'activity' | 'sessions', sub?: 'person' |
 * 'timeline' | 'versions', focus? }` (Data's focus: `{ store, object? }`, the store to open, or `{ view: 'restore', point? }`, its Restore points). Links:
 * `?tab=api|data|traces|jobs|finops|quality|config|mail|terminal|git|changes|activity|sessions` (the old `?tab=config&focus=jobs`
 * opens Jobs)
 * (Traces also keeps the request open in `&trace=<id>`, Activity its service in `&service=<name>`, Files the file it shows in
 * `&file=<path>`: `{ tab: 'files', focus: { path } }`), `?tab=history` (History on its last view), and the old
 * `?history=person|timeline|activity` still lands on the same place.
 */
import { HISTORY_IDS, lastHistorySub } from './projectTabsModel';
import { cleanFilePath, serviceOfFile } from './filesPath';

/** The default row, in order (History stands for Changes, Activity and Sessions). */
export const WORKSPACE_TABS = [
  { id: 'dashboard', label: 'Home' },
  { id: 'preview', label: 'Preview' },
  { id: 'code', label: 'Code' },
  { id: 'history', label: 'History' },
];

export const VIEW_TABS = ['apps', 'files', 'api', 'mcp', 'data', 'traces', 'jobs', 'finops', 'quality', 'config', 'mail', 'terminal', 'git', 'changes', 'activity', 'sessions'];

/**
 * A view as it is shown today: Configuration on its scheduled jobs (`{ tab: 'config', focus: { section: 'jobs' } }`,
 * the old Operate › Jobs, kept by older links and events) is the Jobs tab, on that service if one was named.
 */
export function normalizeView(view) {
  if (view?.tab === 'config' && view.focus?.section === 'jobs') {
    return { tab: 'jobs', sub: undefined, focus: view.focus.service ? { service: view.focus.service } : null };
  }
  return view;
}
export const CHANGES_SUBS = [
  { id: 'person', label: 'By person' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'versions', label: 'Versions' },
];
const CHANGES_SUB_IDS = CHANGES_SUBS.map((s) => s.id);

const SUB_KEY = 'flow.changes.view';

/**
 * Opens a workspace view from anywhere (Canvas listens): `{ tab, focus }` as viewFromHistory reads them, `{}` for
 * Changes on its last view.
 */
export const openHistory = (target = {}) => {
  if (typeof window !== 'undefined') { window.dispatchEvent(new CustomEvent('collab:openHistory', { detail: target })); }
};

/** The key that tells two views apart for Back / Forward: the tab, its sub-view and the Files tab's file. */
export const viewKey = (view) => [view?.tab || '', view?.sub || '', view?.tab === 'files' ? view.focus?.path || '' : ''].join('|');

/**
 * Opens a project file where it belongs (the Home's File gadget, a Markdown link in it): a file of a service in Code on
 * that service, with the file selected (`flow:open-code`, UnifiedTopBar: `?view=files&path=…`, which Canvas turns into
 * the service's Code tab); any other file (the Brain, inputs, results, the root's README) in the Files tab's file view.
 * `filesTab` false (the plan hides Files): Code for every file. Never a folder opener: a file path given as the root of
 * a file tree is listed as a folder and fails ("Not a directory").
 */
export function openProjectFile(path, { filesTab = true } = {}) {
  const file = cleanFilePath(path);
  if (!file || typeof window === 'undefined') { return; }
  if (serviceOfFile(file) || !filesTab) {
    window.dispatchEvent(new CustomEvent('flow:open-code', { detail: { path: file } }));
    return;
  }
  openHistory({ tab: 'files', focus: { path: file } });
}

/** The Changes view last used in this browser: 'person' (default), 'timeline' or 'versions'. */
export function lastChangesSub() {
  try {
    const saved = localStorage.getItem(SUB_KEY);
    return CHANGES_SUB_IDS.includes(saved) ? saved : 'person';
  } catch {
    return 'person';
  }
}

export function rememberChangesSub(sub) {
  try { localStorage.setItem(SUB_KEY, CHANGES_SUB_IDS.includes(sub) ? sub : 'person'); } catch { /* not remembered */ }
}

/**
 * The view an old History request asks for: `{ tab: 'person' | 'timeline' | 'versions' | 'activity' | null, focus }` (the
 * `collab:openHistory` event, `?history=`, the project Home's What changed). No tab: Changes, on its last view.
 */
export function viewFromHistory(tab, focus = null) {
  if (tab === 'history') { return viewFromHistory(lastHistorySub(), focus); }
  if (tab === 'activity') { return { tab: 'activity', focus: focus || null }; }
  if (CHANGES_SUB_IDS.includes(tab)) { return { tab: 'changes', sub: tab, focus: focus || null }; }
  if (VIEW_TABS.includes(tab)) { return normalizeView({ tab, sub: tab === 'changes' ? lastChangesSub() : undefined, focus: focus || null }); }
  return { tab: 'changes', sub: lastChangesSub(), focus: focus || null };
}

/** The view a URL asks for (`?tab=` first, then the old `?history=`), or null. */
export function viewFromSearch(search) {
  const params = new URLSearchParams(search || '');
  const tab = params.get('tab');
  // Activity on some services (`&service=api`, e.g. from a service's page in Apps).
  if (tab === 'activity' && params.get('service')) { return viewFromHistory('activity', { service: params.get('service') }); }
  // Apps on one service (`&service=api`, e.g. from Running now on Flow's Home).
  if (tab === 'apps' && params.get('service')) { return viewFromHistory('apps', { service: params.get('service') }); }
  // The Files tab on one file (`&file=brain/notes.md`).
  if (tab === 'files' && cleanFilePath(params.get('file'))) { return viewFromHistory('files', { path: params.get('file') }); }
  // An older link to Configuration's jobs (`?tab=config&focus=jobs`, or `&section=jobs`): the Jobs tab.
  if (tab === 'config' && (params.get('focus') === 'jobs' || params.get('section') === 'jobs')) { return viewFromHistory('jobs'); }
  if (VIEW_TABS.includes(tab) || tab === 'history') { return viewFromHistory(tab); }
  const history = params.get('history');
  if (history) { return viewFromHistory(history); }
  return null;
}

/**
 * The search string with the view written in (`?tab=`), the old `?history=` dropped; null view: both dropped. The open
 * request of Traces (`&trace=`) only stays while Traces is on, Activity's service (`&service=`) while Activity is, the
 * Files tab's file (`&file=`) while Files shows it.
 */
export function searchWithView(search, view) {
  const params = new URLSearchParams(search || '');
  params.delete('history');
  // The old Configuration-on-its-jobs link's part goes once the view is written.
  if (params.get('focus') === 'jobs') { params.delete('focus'); }
  if (params.get('section') === 'jobs') { params.delete('section'); }
  if (view?.tab !== 'traces') { params.delete('trace'); }
  const file = view?.tab === 'files' ? cleanFilePath(view.focus?.path) : null;
  if (file) { params.set('file', file); } else { params.delete('file'); }
  // Only another workspace view drops it: with no view, `&service=` is the Home Logs view's (Canvas keeps that one).
  if (view && view.tab === 'apps' && typeof view.focus?.service === 'string' && view.focus.service) {
    params.set('service', view.focus.service);
  } else if (view && view.tab !== 'activity') { params.delete('service'); }
  if (view && VIEW_TABS.includes(view.tab)) { params.set('tab', view.tab); } else { params.delete('tab'); }
  const text = params.toString();
  return text ? `?${text}` : '';
}

/**
 * Which tab is on: a workspace view (Changes, Activity and Sessions are History), else the page — Home
 * (Dashboard, or Code for its Files, 'logs' for Logs, which is a tool), a service (Code or Preview).
 */
export function currentWorkspaceTab({ view = null, atHome = true, activeTab = 'preview', homeMode = 'dashboard' } = {}) {
  const shown = normalizeView(view);
  if (shown && VIEW_TABS.includes(shown.tab)) { return HISTORY_IDS.includes(shown.tab) ? 'history' : shown.tab; }
  if (atHome) {
    if (homeMode === 'files') { return 'code'; }
    if (homeMode === 'logs') { return 'logs'; }
    return 'dashboard';
  }
  return activeTab === 'code' ? 'code' : 'preview';
}

const latestView = (person) => [...(person?.views || [])].sort((a, b) => (b.at || 0) - (a.at || 0))[0] || null;

/** The others looking at a preview right now (their latest view is a preview): the Preview tab shows them. */
export function previewWatchers(snapshot) {
  const you = snapshot?.you;
  return (snapshot?.people || [])
    .filter((person) => !you || person.id !== you.id)
    .filter((person) => latestView(person)?.kind === 'preview');
}

/** The Changes tab's count: the changes others (people or their agents, or your agents) made, from a changes answer. */
export function changesCountOf(answer) {
  const youId = answer?.you?.id;
  return (answer?.changes || []).filter((change) => !youId || change.author?.id !== youId || change.agent).length;
}

/** "Owner" / "Editor" / "Viewer" for the header's role chip, or null. */
export function roleLabel(role) {
  if (role === 'owner') { return 'Owner'; }
  if (role === 'editor') { return 'Editor'; }
  if (role === 'viewer') { return 'Viewer'; }
  return null;
}
